import { useState } from 'react';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import { LoadingButton } from '@mui/lab';
import { Card, Grid, IconButton, InputAdornment, Stack } from '@mui/material';
// auth
// components
import { useSnackbar } from 'notistack';
import { useAuthContext } from 'src/auth/useAuthContext';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { ref } from 'yup';
import { translateKey } from 'src/locales/translate';


// ----------------------------------------------------------------------

type FormValuesProps = {
  oldPassword: string;
  password: string;
  repeatPassword: string;
};

export default function AccountChangePassword() {
  const { changePassword } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const RegisterSchema: Yup.Schema<FormValuesProps> = Yup.object().shape({
    oldPassword: Yup.string().required(translateKey("hardcoded.stare_haslo_jest_wymagane")),
    password: Yup.string()
      .required(translateKey("validation.passwordRequired"))
      .min(6, translateKey("hardcoded.haslo_musi_miec_minimum_6_znakow")),
    repeatPassword: Yup.string()
      .required(translateKey("validation.repeatPasswordRequired"))
      .oneOf([ref('password')], translateKey("validation.passwordsMustMatch")),
  });

  const defaultValues = {
    password: '',
    repeatPassword: '',
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(RegisterSchema),
    defaultValues,
  });

  const { handleSubmit, reset } = methods;

  const onSubmit = async ({ password, oldPassword }: FormValuesProps) => {
    setIsLoading(true);
    if (changePassword) {
      try {
        await changePassword(oldPassword, password);
        setIsLoading(false);
        reset();
        enqueueSnackbar(translateKey("hardcoded.haslo_zostalo_zmienione"), {
          variant: 'success',
        });
      } catch (error) {
        setIsLoading(false);
        enqueueSnackbar(error.message || error, {
          variant: 'error',
        });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Stack spacing={3}>
              <RHFTextField
                name="oldPassword"
                label={translateKey("hardcoded.stare_haslo")}
                type={showOldPassword ? 'text' : 'password'}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        edge="end"
                      >
                        <Iconify
                          icon={
                            showOldPassword
                              ? 'eva:eye-fill'
                              : 'eva:eye-off-fill'
                          }
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <RHFTextField
                name="password"
                label={translateKey("auth.fieldPassword")}
                helperText={translateKey("hardcoded.haslo_musi_miec_minimum_6_znakow")}
                type={showPassword ? 'text' : 'password'}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        <Iconify
                          icon={
                            showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'
                          }
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <RHFTextField
                name="repeatPassword"
                label={translateKey("auth.fieldRepeatPassword")}
                type={showRepeatPassword ? 'text' : 'password'}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowRepeatPassword(!showRepeatPassword)
                        }
                        edge="end"
                      >
                        <Iconify
                          icon={
                            showRepeatPassword
                              ? 'eva:eye-fill'
                              : 'eva:eye-off-fill'
                          }
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <Stack flexDirection="row" justifyContent="flex-end" sx={{ mt: 3 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isLoading}
              >
                <span>{translateKey("common.save")}</span>
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
